/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { useOktaAuth } from "@okta/okta-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import notifications from "../../container/notifications";
import { useLogout } from "../../hooks";
import {
  AccountDetailsInput,
  FewAccountDetailsDocument,
  useAcceptPoliciesMutation,
} from "../../types/generated/graphql";
import { TermsAndConditionsModalProps } from "./terms-and-conditions-modal.types";

export const useTermsAndConditionsModal = (): TermsAndConditionsModalProps => {
  const { t } = useTranslation();
  const { logout } = useLogout();
  const {
    oktaAuth: { tokenManager },
  } = useOktaAuth();
  const [acceptPolicies] = useAcceptPoliciesMutation({
    refetchQueries: [FewAccountDetailsDocument],
  });

  const handleSubmit = useCallback(
    async ({ privacyPolicyAccepted, termsAccepted, rulesAccepted }: AccountDetailsInput) => {
      try {
        await acceptPolicies({
          variables: {
            input: { privacyPolicyAccepted, termsAccepted, rulesAccepted },
          },
        });
        await tokenManager.renew("accessToken");
      } catch (error) {
        notifications.error({
          description: t("Something went wrong when accepting the terms and conditions."),
        });
      }
    },
    [t, acceptPolicies, tokenManager],
  );

  return {
    handleSubmit,
    handleCancel: logout,
  };
};
